<template>
  <div class="chat-chart-container">
    <Chart
      :type="chartData.type || 'bar'"
      :series="chartData.series"
      :options="chartOptions"
      :loading="false"
      :height="`${chartHeight}px`"
      :chart-height="`${chartHeight - 20}px`"
      width="100%"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Chart from '@/components/Chart.vue';
import type { ApexOptions } from 'apexcharts';

type ChartSeries = Array<{ name?: string; data: (number | null)[] }> | number[];

export interface ChatChartData {
  type?: 'bar' | 'line' | 'area' | 'donut' | 'pie' | 'radar';
  series: ChartSeries;
  title?: string;
  xaxis?: { categories?: string[] };
  labels?: string[];
  colors?: string[];
}

const props = defineProps<{
  chartData: ChatChartData;
}>();

const isPieOrDonut = computed(() => props.chartData.type === 'pie' || props.chartData.type === 'donut');

const chartHeight = computed(() => {
  const baseHeight = 200;

  // For pie/donut charts, adjust height based on number of slices (legend items)
  if (isPieOrDonut.value) {
    const labelCount = props.chartData.labels?.length || 0;
    const seriesCount = Array.isArray(props.chartData.series) ? props.chartData.series.length : 0;
    const itemCount = Math.max(labelCount, seriesCount);

    // Each legend item needs ~20-25px, add extra height for many items
    if (itemCount > 6) {
      return baseHeight + Math.min(itemCount * 8, 120); // Cap at 320px
    } else if (itemCount > 4) {
      return baseHeight + 40; // 240px for moderate items
    }
  }
  // For bar/line/area charts, adjust height based on number of categories and label length
  else {
    const categories = props.chartData.xaxis?.categories || [];
    const categoryCount = categories.length;
    const hasLongLabels = categories.some((label) => label && label.length > 15);

    // Add extra height if there are many categories or long labels
    if (hasLongLabels || categoryCount > 8) {
      return baseHeight + 80; // 280px for long labels
    }
  }

  return baseHeight;
});

const chartOptions = computed<ApexOptions>(() => {
  const options: ApexOptions = {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      background: 'transparent',
    },
    title: props.chartData.title
      ? {
          text: props.chartData.title,
          style: { fontSize: '12px', fontWeight: 500 },
        }
      : undefined,
    colors: props.chartData.colors,
    legend: {
      fontSize: '10px',
      position: 'bottom',
    },
    dataLabels: {
      enabled: isPieOrDonut.value,
      style: { fontSize: '10px' },
    },
  };

  if (isPieOrDonut.value) {
    options.labels = props.chartData.labels;
  } else {
    options.xaxis = {
      ...props.chartData.xaxis,
      labels: {
        rotate: -45,
        rotateAlways: false,
        trim: false,
        style: {
          fontSize: '10px',
        },
      },
      tooltip: {
        enabled: true,
      },
    };
    options.grid = {
      strokeDashArray: 3,
      borderColor: 'rgba(128, 128, 128, 0.2)',
    };
  }

  return options;
});
</script>

<style scoped>
.chat-chart-container {
  width: 100%;
  min-height: 200px;
  margin: 0.5rem 0;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 0.5rem;
  direction: ltr;
}

:deep(.apexcharts-canvas) {
  background: transparent !important;
}

.dark .chat-chart-container {
  background: rgba(0, 0, 0, 0.2);
}
</style>
